import type React from 'react';
import { Ellipsis } from 'lucide-react';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTextEntryFocused } from '@/hooks/use-text-entry-focused';
import { getTabBarItems, isNavItemActive } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { NAV_ITEM_ICONS, useNavItemLabel } from './nav-items';

export interface MobileTabBarProps {
  tenantSlug: string;
  pathname: string;
  /** `isMoreSectionActive(...)`: the route is one of the Mais destinations. */
  moreActive: boolean;
  onMoreClick: () => void;
  /** Focus returns here when the Mais sheet closes. */
  moreButtonRef?: React.Ref<HTMLButtonElement>;
}

const itemClassName =
  'flex min-h-touch flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium leading-none text-muted-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';

/**
 * Main navigation below 768px (SHELL-01). In the flow of the shell column,
 * not fixed; hidden while the on-screen keyboard is up (SHELL-10).
 */
export function MobileTabBar({
  tenantSlug,
  pathname,
  moreActive,
  onMoreClick,
  moreButtonRef,
}: MobileTabBarProps) {
  const { LL } = useI18nContext();
  const labelOf = useNavItemLabel();
  const textEntryFocused = useTextEntryFocused();

  if (textEntryFocused) return null;

  return (
    <nav
      aria-label={LL.shell.tabs.ariaLabel()}
      className="shrink-0 border-t bg-background pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex h-tab-bar items-stretch px-1">
        {getTabBarItems(tenantSlug).map((item) => {
          const Icon = NAV_ITEM_ICONS[item.id];
          const active = isNavItemActive(item.href, pathname);
          return (
            <Link
              key={item.id}
              to={item.href}
              aria-current={active ? 'page' : undefined}
              className={cn(itemClassName, active && 'text-primary')}
            >
              <Icon className="size-5" strokeWidth={active ? 2.25 : 2} />
              <span>{labelOf(item.id)}</span>
            </Link>
          );
        })}
        <button
          ref={moreButtonRef}
          type="button"
          aria-haspopup="dialog"
          data-active={moreActive || undefined}
          onClick={onMoreClick}
          className={cn(itemClassName, 'data-[active]:text-primary')}
        >
          <Ellipsis className="size-5" />
          <span>{LL.shell.tabs.more()}</span>
        </button>
      </div>
    </nav>
  );
}
